import type { Expression, GeoJSONSource, LineLayerSpecification, Map as MapboxMap } from 'mapbox-gl'
import { ATHLETE_LAYER_ID } from './athleteOverlay'

export const ROUTE_SOURCE_ID = 'lv-route-source'
export const ROUTE_GLOW_LAYER_ID = 'lv-route-glow'
export const ROUTE_CASING_LAYER_ID = 'lv-route-casing'
export const ROUTE_LINE_LAYER_ID = 'lv-route'

/** The course line's colour when the event has one course. */
export const ROUTE_COLOR = '#17D5FF'
/** Play ink: the casing each of several courses wears, as the stacked mark's layers wear their outline. */
const COURSE_CASING = '#171916'

type RouteLineFeature = GeoJSON.Feature<GeoJSON.LineString>

/** Each course of an event with several: its line with `color` and `order` (higher draws on top). A lone course's feature carries neither. */
export interface CourseLineProperties {
  color: string
  order: number
}

export type RouteOverlayData = RouteLineFeature | GeoJSON.FeatureCollection<GeoJSON.LineString, CourseLineProperties>

const drawable = (feature: RouteLineFeature) => feature.geometry.coordinates.length >= 2

export function hasDrawableRoute(data: RouteOverlayData | null | undefined): data is RouteOverlayData {
  if (!data) return false
  return data.type === 'FeatureCollection' ? data.features.some(drawable) : drawable(data)
}

/** A course with its own colour: one of several. */
const coloured: Expression = ['has', 'color']

/**
 * A lone course: one cyan line with a glow. Several: each in its own Play colour on an ink casing, which keeps yellow
 * readable on light ground and lines apart where courses cross; stacked by `order`.
 */
export function routeOverlayLayerSpecs(): LineLayerSpecification[] {
  const layout = { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['coalesce', ['get', 'order'], 0] } as LineLayerSpecification['layout']
  return [
    {
      id: ROUTE_GLOW_LAYER_ID,
      type: 'line',
      source: ROUTE_SOURCE_ID,
      slot: 'top',
      filter: ['!', coloured],
      paint: {
        'line-color': ROUTE_COLOR,
        'line-width': 18,
        'line-opacity': 0.38,
        'line-blur': 10,
        'line-emissive-strength': 1,
      },
      layout,
    },
    {
      id: ROUTE_CASING_LAYER_ID,
      type: 'line',
      source: ROUTE_SOURCE_ID,
      slot: 'top',
      filter: coloured,
      paint: {
        'line-color': COURSE_CASING,
        'line-width': 8,
        'line-emissive-strength': 1,
      },
      layout,
    },
    {
      id: ROUTE_LINE_LAYER_ID,
      type: 'line',
      source: ROUTE_SOURCE_ID,
      slot: 'top',
      paint: {
        'line-color': ['coalesce', ['get', 'color'], ROUTE_COLOR],
        'line-width': ['case', coloured, 4, 5],
        'line-opacity': 1,
        'line-emissive-strength': 1,
      },
      layout,
    },
  ]
}

export function removeRouteOverlay(map: MapboxMap) {
  for (const layerId of [ROUTE_LINE_LAYER_ID, ROUTE_CASING_LAYER_ID, ROUTE_GLOW_LAYER_ID]) {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId)
    }
  }

  if (map.getSource(ROUTE_SOURCE_ID)) {
    map.removeSource(ROUTE_SOURCE_ID)
  }
}

/** Each course layer's line opacity, as drawn and as a search fades it back: [full, faded]. */
const ROUTE_OPACITY: Record<string, [full: number, faded: number]> = {
  [ROUTE_GLOW_LAYER_ID]: [0.38, 0.08],
  [ROUTE_CASING_LAYER_ID]: [1, 0.25],
  [ROUTE_LINE_LAYER_ID]: [1, 0.25],
}

/**
 * Fades the course back while a search picks athletes out (docs/features/athletes-and-trackers.md, Map), or brings it
 * back; Mapbox eases the change. Only an opacity that differs is set, so CourseMap can call it on every idle without
 * keeping the map drawing.
 */
export function fadeRouteOverlay(map: MapboxMap, faded: boolean) {
  try {
    for (const [layerId, [full, back]] of Object.entries(ROUTE_OPACITY)) {
      if (!map.getLayer(layerId)) continue
      const opacity = faded ? back : full
      if ((map.getPaintProperty(layerId, 'line-opacity') ?? 1) !== opacity) map.setPaintProperty(layerId, 'line-opacity', opacity)
    }
  } catch {
    // The style is changing; the next sync fades it again.
  }
}

/**
 * The lines each route source holds. CourseMap syncs on every idle, and setting a source's data again, even the same
 * lines, makes Mapbox reload its tiles and redraw, which ends in another idle: the map would never stop drawing.
 */
const sourceLines = new WeakMap<object, RouteOverlayData>()

export function syncRouteOverlay(map: MapboxMap, data: RouteOverlayData | null | undefined): boolean {
  if (!hasDrawableRoute(data)) {
    removeRouteOverlay(map)
    return false
  }

  try {
    const source = map.getSource(ROUTE_SOURCE_ID) as GeoJSONSource | undefined
    if (!source) {
      map.addSource(ROUTE_SOURCE_ID, {
        type: 'geojson',
        data,
      })
    } else if (sourceLines.get(source) !== data) {
      source.setData(data)
    }
    const synced = map.getSource(ROUTE_SOURCE_ID)
    if (synced) sourceLines.set(synced, data)

    // Under the athletes' dots when they arrived first, so the lines never cover them.
    const below = map.getLayer(ATHLETE_LAYER_ID) ? ATHLETE_LAYER_ID : undefined
    for (const layer of routeOverlayLayerSpecs()) {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer, below)
      }
    }

    return true
  } catch {
    return false
  }
}
